import React, { useEffect, useState } from "react";
import { Form, FormField, Button, Comment, Loader } from "semantic-ui-react";
import MultipleTrigger from "@common/components/shared/Mention/MultipleTrigger";
import ReviewClient from "@common/api/ReviewClient";
import CommentClient from "@common/api/CommentClient";
import CustomerClient from "@common/api/CustomerClient";
import { handleError } from "@common/components/helpers/error";
import LoadingAction from "@common/redux/actions/LoadingAction";
import { Mention } from "@common/components/shared/Mention";
import defaultMentionStyle from "@common/components/shared/Mention/defaultMentionStyle";
import { getFormattedDate } from "@common/components/helpers/datetime";
import { getInitials, parseTaggedPeople, mentionMarkupToPlainText, mapAssigneesToMentionData } from "@common/components/helpers/comment";

const Comments = ({ customerId }) => {
  const [assignees, setAssignees] = useState([]);
  const [customerComments, setCustomerComments] = useState([]);
  const [commentValue, setCommentValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loadingComments, setLoadingComments] = useState(false);

  useEffect(() => {
    LoadingAction.set(true).then(() => {
    ReviewClient.Assignees()
      .then((data) => {
        setAssignees(mapAssigneesToMentionData(data));
      })
        .catch((err) => handleError(err))
        .finally(() => LoadingAction.set(false));
    });
  }, []);

  useEffect(() => {
    if (!customerId) {
      setCustomerComments([]);
      return;
    }

    setLoadingComments(true);
    CustomerClient.Read(customerId)
      .then((data) => {
        setCustomerComments(data?.customerComments ?? []);
      })
      .catch((err) => handleError(err))
      .finally(() => setLoadingComments(false));
  }, [customerId]);

  const handleChange = (_ev, newValue) => {
    setCommentValue(newValue ?? "");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!customerId) return;
    const text = mentionMarkupToPlainText(commentValue).trim();
    if (!text) return;

    const payload = {
      customerId,
      text,
      taggedPeople: parseTaggedPeople(commentValue),
      createdDate: new Date().toISOString(),
    };

    setSubmitting(true);
    CommentClient.Create(payload)
      .then(() => {
        setCommentValue("");
      })
      .then(() => {
        if (!customerId) return;
        setLoadingComments(true);
        return CustomerClient.Read(customerId)
          .then((data) => setCustomerComments(data?.customerComments ?? []))
          .catch((err) => handleError(err))
          .finally(() => setLoadingComments(false));
      })
      .catch((err) => handleError(err))
      .finally(() => setSubmitting(false));
  };

  const renderCommentText = (text, taggedPeople) => {
    const list = Array.isArray(taggedPeople) ? taggedPeople : [];
    const safeText = String(text ?? "");
    if (list.length === 0) return safeText;

    const byName = new Map();
    list.forEach((p) => {
      const name = String(p?.name ?? "").trim();
      if (name && !byName.has(name)) byName.set(name, p);
    });
    const namesSorted = [...byName.keys()].sort((a, b) => b.length - a.length);

    const nodes = [];
    let i = 0;
    let seg = 0;
    while (i < safeText.length) {
      if (safeText[i] === "@") {
        let matchedName = null;
        for (const name of namesSorted) {
          const token = `@${name}`;
          if (safeText.startsWith(token, i)) {
            matchedName = name;
            break;
          }
        }
        if (matchedName) {
          const token = `@${matchedName}`;
          if (seg < i) nodes.push(safeText.slice(seg, i));
          nodes.push(
            <Mention
              key={`${byName.get(matchedName)?.id ?? "p"}-${matchedName}-${i}`}
              display={matchedName}
              style={defaultMentionStyle}
            />,
          );
          i += token.length;
          seg = i;
          continue;
        }
      }
      i += 1;
    }
    if (seg < safeText.length) nodes.push(safeText.slice(seg));

    return nodes;
  };

  return (
    <Comment.Group>
      <Form onSubmit={handleSubmit}>
        <FormField>
          <span>Add a comment (use @ to mention someone)</span>
          <MultipleTrigger
            value={commentValue}
            onChange={handleChange}
            data={assignees}
          />
        </FormField>
        <FormField className="flex-right">
          <Button
            positive
            type="submit"
            loading={submitting}
            disabled={commentValue.trim() === "" || !customerId}
          >
            Post Comment
          </Button>
        </FormField>
      </Form>
      {loadingComments ? (
        <div style={{ padding: "0.5rem 0" }}>
          <Loader active inline size="small" content="Loading comments…" />
        </div>
      ) : customerComments.length > 0 ? (
        customerComments.map((comment) => (
          <Comment key={comment.id ?? `${comment.createdDate}-${comment.text}`}>
            <div className="avatar">
              <div className="avatar-initials">
                {getInitials(comment?.commenter?.name)}
              </div>
            </div>
            <Comment.Content>
              <Comment.Author as="a">
                {comment?.commenter?.name ?? "Unknown"}
              </Comment.Author>
              <Comment.Metadata>
                <div>{getFormattedDate(comment?.createdDate)}</div>
              </Comment.Metadata>
              <Comment.Text>
                {renderCommentText(comment?.text, comment?.taggedPeople)}
              </Comment.Text>
            </Comment.Content>
          </Comment>
        ))
      ) : (
        <div style={{ padding: "0.5rem 0", color: "#888" }}>
          No comments yet.
        </div>
      )}
    </Comment.Group>
  );
};

export default Comments;
